import { defineCustomElement } from 'vue'
import CollapsiblePanel from '~/components/custom-elements/CollapsiblePanel.ce.vue'
import DownloadAsset from '~/components/custom-elements/DownloadAsset.ce.vue'
import DownloadDocument from '~/components/custom-elements/DownloadDocument.ce.vue'
import GatedContent from '~/components/custom-elements/GatedContent.ce.vue'
import ProductCard from '~/components/custom-elements/ProductCard.ce.vue'
import WpnCta from '~/components/custom-elements/WpnCta.ce.vue'
import WpnEventBtn from '~/components/custom-elements/WpnEventBtn.ce.vue'
import type { CustomElementsDependencies } from '~/types/custom-elements'

declare global {
  interface Window {
    __WPN_CUSTOM_ELEMENTS__?: CustomElementsDependencies
  }
}

export default defineNuxtPlugin((nuxtApp) => {
  const i18n = nuxtApp.$i18n as { locale?: { value?: string } } | undefined

  const fallbackUserAccessCheck: CustomElementsDependencies['userAccessCheck'] =
    (level, highestUserLevel) => {
      if (!level) return true
      const levelNumber = parseInt(level.split(' ')[1] || '', 10)
      if (Number.isNaN(levelNumber)) return true
      return highestUserLevel >= levelNumber
    }

  window.__WPN_CUSTOM_ELEMENTS__ = {
    client: nuxtApp.$client as CustomElementsDependencies['client'],
    checkVisibility: (...args) => {
      const checkVisibility = nuxtApp.$checkVisibility as
        | CustomElementsDependencies['checkVisibility']
        | undefined
      if (!checkVisibility) return true
      return checkVisibility(...args)
    },
    visibilityDecision: (...args) => {
      const visibilityDecision = nuxtApp.$visibilityDecision as
        | CustomElementsDependencies['visibilityDecision']
        | undefined
      if (!visibilityDecision) return { visible: true }
      return visibilityDecision(...args)
    },
    userAccessCheck: (...args) => {
      const userAccessCheck = nuxtApp.$userAccessCheck as
        | CustomElementsDependencies['userAccessCheck']
        | undefined
      if (!userAccessCheck) return fallbackUserAccessCheck(...args)
      return userAccessCheck(...args)
    },
    getHighestUserLevel: () => useUserStore().highestUserLevel,
    getLocale: () => i18n?.locale?.value ?? 'en',
  }

  if (!customElements.get('download-asset')) {
    customElements.define(
      'download-asset',
      defineCustomElement(DownloadAsset, {
        shadowRoot: false,
      }),
    )
  }

  // Compatibility alias for alternate authored content.
  if (!customElements.get('downloadable-asset')) {
    customElements.define(
      'downloadable-asset',
      defineCustomElement(DownloadAsset, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('gated-content')) {
    customElements.define(
      'gated-content',
      defineCustomElement(GatedContent, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('download-document')) {
    customElements.define(
      'download-document',
      defineCustomElement(DownloadDocument, {
        shadowRoot: false,
      }),
    )
  }

  // Compatibility alias for alternate authored content.
  if (!customElements.get('downloadable-document')) {
    customElements.define(
      'downloadable-document',
      defineCustomElement(DownloadDocument, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('product-card')) {
    customElements.define(
      'product-card',
      defineCustomElement(ProductCard, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('collapsible-panel')) {
    customElements.define(
      'collapsible-panel',
      defineCustomElement(CollapsiblePanel, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('wpn-cta')) {
    customElements.define(
      'wpn-cta',
      defineCustomElement(WpnCta, {
        shadowRoot: false,
      }),
    )
  }

  if (!customElements.get('wpn-event-btn')) {
    customElements.define(
      'wpn-event-btn',
      defineCustomElement(WpnEventBtn, {
        shadowRoot: false,
      }),
    )
  }
})
